/* Navegación espacial para mando a distancia / teclado.
   - Calcula el siguiente elemento enfocable por geometría (dirección + alineación).
   - Restringe el foco a la "capa" activa: reproductor > modal F7 abierto > página actual.
   - Los contenedores con [data-nav-row] (filas horizontales) priorizan el movimiento dentro de la fila
     y se desplazan para mantener el elemento visible.
   - Enter/OK activa; Back/Escape delega en el manejador global de "atrás". */

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex], [data-focusable], .actions-button, .dialog-button, .sheet-modal .list li';
/* Listas verticales (menús, hojas de acciones, selector de categorías): arriba/abajo pasan al elemento anterior o
   siguiente de la lista, sin depender de la geometría (con zoom o en pantallas raras la geometría engaña). */
const LIST_SELECTOR = '[data-nav-list], .actions-modal, .actions-popover, .popover-inner, .catpick-list';

const LONG_PRESS_MS = 500;

const KEY_MAP = {
    ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
    Enter: 'enter', ' ': 'enter', NumpadEnter: 'enter',
    Escape: 'back', Backspace: 'back', GoBack: 'back', BrowserBack: 'back', XF86Back: 'back',
    // Códigos de teclas de TVs (Tizen / webOS / algunos Android TV)
    10009: 'back', 461: 'back', 8: 'back', 27: 'back', 13: 'enter', 37: 'left', 38: 'up', 39: 'right', 40: 'down',
    // Tecla de menú del mando (Android TV, Fire TV): las opciones de la tarjeta o fila enfocada, como mantener OK.
    ContextMenu: 'menu', 82: 'menu'
};

export class TvNav {
    constructor() {
        this.enabled = false;
        this.current = null;
        this.memory = new WeakMap();
        this.onBack = null;
        this.onKey = null;
        this.onEnter = null;
        this.onTextField = null; // OK sobre un campo de texto sin foco: la app puede ofrecer escribir con el móvil (devuelve true si se ocupa ella)
        // OK mantenido sobre un elemento que case con `longPressSelector` → onLongPress(el) (opciones de una tarjeta).
        this.onLongPress = null;
        this.longPressSelector = '';
        this._hold = null;
        this._keyupSeen = false;
        this._handler = this._onKeyDown.bind(this);
        this._upHandler = this._onKeyUp.bind(this);
        this._blurHandler = () => this._holdClear();
        this._clickHandler = this._onPointer.bind(this);
        this.layerResolver = null;
    }

    enable() {
        if (this.enabled) return;
        this.enabled = true;
        document.addEventListener('keydown', this._handler, true);
        document.addEventListener('keyup', this._upHandler, true);
        document.addEventListener('pointerdown', this._clickHandler, true);
        window.addEventListener('blur', this._blurHandler);
    }

    disable() {
        this.enabled = false;
        document.removeEventListener('keydown', this._handler, true);
        document.removeEventListener('keyup', this._upHandler, true);
        window.removeEventListener('blur', this._blurHandler);
        document.removeEventListener('pointerdown', this._clickHandler, true);
        this._holdClear();
        this._clearFocus();
    }

    /** Capa activa: el resolver externo decide (player, modal, página). */
    layer() {
        if (this.layerResolver) {
            const el = this.layerResolver();
            if (el) return el;
        }
        return document.querySelector('.page-current') || document.body;
    }

    candidates(layer) {
        const root = layer || this.layer();
        let nodes = Array.from(root.querySelectorAll(FOCUSABLE));
        // La barra de navegación vive fuera de la página: se incluye cuando la capa activa es una página.
        if (root.classList && root.classList.contains('page')) {
            const nav = document.querySelector('.appnav:not(.is-hidden)');
            if (nav) nodes = nodes.concat(Array.from(nav.querySelectorAll(FOCUSABLE)));
        }
        const out = [];
        for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i];
            // tabindex="-1" excluye, salvo el que pone focus() para poder enfocar divs (menús, diálogos): si no,
            // cada elemento visitado desaparecía del mapa y no se podía volver a él (bajar sí, subir no).
            if (el.disabled || (el.getAttribute('tabindex') === '-1' && !el.hasAttribute('data-nav-tab')) || el.getAttribute('aria-hidden') === 'true') continue;
            if (el.closest('[data-nav-skip]')) continue;
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) continue;
            const style = window.getComputedStyle(el);
            if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') continue;
            out.push({ el, rect });
        }
        return out;
    }

    _clearFocus() {
        document.querySelectorAll('.is-focused').forEach((el) => el.classList.remove('is-focused'));
    }

    _isTextField(el) {
        return !!el && /^(INPUT|TEXTAREA)$/.test(el.tagName) && !/^(range|checkbox|radio|button|submit)$/.test(el.type || '');
    }

    focus(el, { scroll = true } = {}) {
        if (!el) return false;
        this._clearFocus();
        this.current = el;
        if (!el.closest('.appnav')) this._lastContent = el;
        el.classList.add('is-focused');
        if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('data-nav-tab', '');
        }
        // Un campo de texto solo recibe foco real (y abre el teclado de la TV) al pulsar OK sobre
        // él: si se enfocara al pasar con las flechas, el teclado saltaría y rompería la navegación.
        if (this._isTextField(el)) {
            if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
        } else {
            try {
                el.focus({ preventScroll: true });
            } catch (e) {
                el.focus();
            }
        }
        const row = el.closest('[data-nav-row]');
        const scroller = row || el.closest('.page-content, .player-side-body, .sheet-modal-inner, .popup .page-content');
        this.memory.set(this.layer(), el);
        if (scroll) {
            // En la cabecera destacada (Inicio, Películas, Series) no se centra el botón (eso desplazaba la página y la imagen
            // se veía cortada y «saltaba»): se deja la cabecera entera a la vista, arriba.
            if (el.closest('.hero')) {
                const sc = el.closest('.page-content');
                if (sc && sc.scrollTop > 0) sc.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                el.scrollIntoView({ block: 'center', inline: row ? 'center' : 'nearest', behavior: 'smooth' });
            }
        }
        el.dispatchEvent(new CustomEvent('nav:focus', { bubbles: true }));
        return true;
    }

    /**
     * Ejecuta `redraw` (que vuelve a pintar una zona de la pantalla) y deja el foco en el mismo botón de antes. Sin esto, al
     * pulsar OK en algo que redibuja la página (un favorito, «marcar visto», un filtro…) el botón se destruye, el foco se pierde y
     * la siguiente flecha salta a otro sitio. El botón se reconoce por su atributo de acción (data-fav, data-filter…).
     */
    preserve(redraw, scope) {
        const root = scope || this.layer() || document;
        const inScope = (el) => !!el && el.nodeType === 1 && root.contains(el);
        // El botón que tiene el foco ahora o, si el foco está en una ventana abierta desde él (el selector de un filtro), el que tenía
        // antes de abrirla: es al que hay que volver cuando la ventana se cierre.
        const cur = this.current;
        const wasFocused = inScope(cur); // antes de redibujar: después el botón ya no cuelga de la zona
        let sel = wasFocused ? this._selectorOf(cur) : '';
        let memoryLayers = [];
        [this.layer(), document.querySelector('.page-current')].filter((l, i, a) => l && a.indexOf(l) === i).forEach((layer) => { const el = this.memory.get(layer); if (inScope(el)) { memoryLayers.push(layer); if (!sel) sel = this._selectorOf(el); } });
        const result = redraw();
        const restore = () => {
            if (!sel) return;
            const again = root.querySelector(sel);
            if (!again) return;
            memoryLayers.forEach((layer) => this.memory.set(layer, again));
            if (wasFocused && (!this.current || !document.contains(this.current))) this.focus(again, { scroll: false });
        };
        restore();
        if (result && typeof result.then === 'function') result.then(restore).catch(() => {});
        return result;
    }

    _selectorOf(el) {
        if (!el || !el.getAttribute) return '';
        const attrs = ['data-fav', 'data-watch-season', 'data-season', 'data-filter', 'data-view', 'data-surprise', 'data-play-episode', 'data-play', 'data-more', 'data-trailer', 'data-watch-ep', 'data-cast-toggle', 'data-setting', 'data-nav', 'data-provider-toggle', 'data-open', 'data-actor', 'data-info', 'data-plot-toggle', 'data-play-live', 'data-hero-play', 'data-hero-step', 'data-hero-go'];
        for (const a of attrs) {
            if (!el.hasAttribute(a)) continue;
            const v = el.getAttribute(a);
            const esc = (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
            return v === '' ? '[' + a + ']' : '[' + a + '="' + esc + '"]';
        }
        return '';
    }

    /** Enfoca el elemento recordado en la capa, o el primero disponible / el preferido. */
    focusLayer(layer, preferred) {
        const root = layer || this.layer();
        // Si hay otra capa activa (un diálogo o un popup abiertos, el reproductor), una página que
        // se repinta no puede robarle el foco.
        const active = this.layer();
        if (layer && active && active !== root && document.contains(active) && !root.contains(active) && !active.contains(root)) return false;
        const remembered = this.memory.get(root);
        if (remembered && root.contains(remembered) && this._isVisible(remembered)) return this.focus(remembered);
        if (preferred && root.contains(preferred)) return this.focus(preferred);
        const auto = root.querySelector('[data-nav-default]');
        if (auto && this._isVisible(auto)) return this.focus(auto);
        const list = this.candidates(root);
        if (list.length) return this.focus(list[0].el);
        return false;
    }

    _isVisible(el) {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
    }

    _onPointer(event) {
        // Con ratón/táctil, sincronizamos el foco visual sin robar interacción.
        const target = event.target.closest && event.target.closest(FOCUSABLE);
        if (target) {
            this._clearFocus();
            this.current = target;
        }
    }

    _onKeyDown(event) {
        // Los códigos numéricos de KEY_MAP son claves de objeto (texto): sin este filtro, teclear un «8»
        // en un campo se leía como la tecla «atrás» (código 8) y le quitaba el foco.
        const printable = typeof event.key === 'string' && event.key.length === 1 && event.key !== ' ';
        let action = printable ? undefined : (KEY_MAP[event.key] || KEY_MAP[event.keyCode]);
        // Números del mando (keyCode 48-57 / 96-105) aunque `key` llegue vacío o «Unidentified»: no son navegación.
        if (!printable && ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105))) action = undefined;
        if (!action) {
            if (this.onKey && this.onKey(event)) event.preventDefault();
            return;
        }
        // OK mantenido en curso: las repeticiones no hacen nada más (si ya saltaron las opciones, el foco está en el menú y una
        // repetición pulsaría su primera entrada). Una pulsación nueva sin haber soltado (keyup perdido) empieza de cero.
        if (this._hold && action === 'enter') {
            if (event.repeat) { event.preventDefault(); this._holdCheck(this._hold.target); return; }
            this._holdClear();
        }
        const active = document.activeElement;
        const inText = active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) && !(active.type === 'range' || active.type === 'checkbox');

        // Si la capa activa cambió (se abrió un diálogo, se cerró un popup) y el elemento marcado
        // quedó fuera de ella, la primera tecla lleva el foco dentro en lugar de actuar sobre lo viejo.
        if (!inText) {
            const layer = this.layer();
            if (!this.current || !document.contains(this.current) || !this._inLayer(layer, this.current)) {
                if (this.focusLayer(layer) && action !== 'back') {
                    event.preventDefault();
                    return;
                }
            }
        }

        if (inText) {
            if (action === 'back' && event.key === 'Backspace' && active.value) return; // borrar texto
            if (action === 'left' || action === 'right') return; // mover el cursor
            if (action === 'enter' && active.tagName === 'TEXTAREA') return;
            if (action === 'back') {
                event.preventDefault();
                active.blur();
                this.focus(active);
                return;
            }
        }

        if (active && active.type === 'range' && (action === 'left' || action === 'right')) return;

        if (action === 'back') {
            event.preventDefault();
            if (this.onBack) this.onBack(event);
            return;
        }

        if (action === 'menu') {
            const target = this.current && document.contains(this.current) ? this.current : null;
            const card = target && this.onLongPress && this.longPressSelector ? target.closest(this.longPressSelector) : null;
            if (card) {
                event.preventDefault();
                if (!event.repeat) this.onLongPress(card);
            }
            return;
        }

        if (action === 'enter') {
            const target = this.current && document.contains(this.current) ? this.current : active;
            if (!target || target === document.body) {
                this.focusLayer();
                event.preventDefault();
                return;
            }
            if (this._isTextField(target)) {
                if (document.activeElement !== target) {
                    event.preventDefault();
                    // La app puede ofrecer otra forma de escribir (con el móvil) antes de abrir el teclado de la tele.
                    if (this.onTextField && this.onTextField(target, event)) return;
                    target.focus();
                }
                return;
            }
            if (/^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
            // El reproductor puede quedarse con el OK antes de que pulse el botón enfocado (con los controles ocultos, OK = lista de canales).
            if (this.onEnter && this.onEnter(event, target)) {
                event.preventDefault();
                return;
            }
            // Pulsación larga de OK sobre una tarjeta: opciones (Mi lista…). Solo cuando el mando manda keyup (se sabe
            // con la primera pulsación); si no, OK sigue actuando al instante como siempre.
            if (this.onLongPress && this.longPressSelector && this._keyupSeen && target.matches(this.longPressSelector)) {
                event.preventDefault();
                if (event.repeat) { this._holdCheck(target); return; }
                this._holdClear();
                this._hold = { target, at: Date.now(), fired: false, timer: setTimeout(() => this._holdCheck(target), LONG_PRESS_MS) };
                return;
            }
            event.preventDefault();
            target.click();
            return;
        }

        if (this.onKey && this.onKey(event, action)) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        this.move(action);
    }

    _holdCheck(target) {
        const h = this._hold;
        if (!h || h.fired || h.target !== target || Date.now() - h.at < LONG_PRESS_MS - 20) return;
        h.fired = true;
        clearTimeout(h.timer);
        // Si el keyup no llegara (mando raro, pérdida de foco), la pulsación no queda colgada para siempre.
        h.timer = setTimeout(() => { if (this._hold === h) this._hold = null; }, 2500);
        if (document.contains(target)) this.onLongPress(target);
    }

    _holdClear() {
        if (this._hold) { clearTimeout(this._hold.timer); this._hold = null; }
    }

    /** Al soltar OK: si fue corto, el clic de siempre; si ya saltaron las opciones, nada. */
    _onKeyUp(event) {
        const action = KEY_MAP[event.key] || KEY_MAP[event.keyCode];
        if (action !== 'enter') return;
        this._keyupSeen = true;
        const h = this._hold;
        if (!h) return;
        this._hold = null;
        clearTimeout(h.timer);
        event.preventDefault();
        if (!h.fired && document.contains(h.target)) h.target.click();
    }

    _inLayer(layer, el) {
        if (!el) return false;
        if (layer.contains(el)) return true;
        const nav = el.closest('.appnav');
        return !!(nav && layer.classList && layer.classList.contains('page'));
    }

    /** ¿La barra de navegación está a un lado (temas Quiosco…)? Entonces es una lista vertical, no una fila. */
    _sideNav() {
        const cl = document.documentElement && document.documentElement.classList;
        return !!(cl && typeof cl.contains === 'function' && cl.contains('layout-nav-side') && typeof document.querySelector === 'function' && document.querySelector('.appnav:not(.is-hidden)'));
    }

    /** Elementos de la barra lateral de arriba abajo. */
    _navItems(layer) {
        const nav = document.querySelector('.appnav:not(.is-hidden)');
        if (!nav) return [];
        return this.candidates(layer).filter((c) => nav.contains(c.el)).sort((a, b) => (a.rect.top - b.rect.top) || (a.rect.left - b.rect.left)).map((c) => c.el);
    }

    /** Barra lateral: arriba/abajo recorren sus entradas, derecha vuelve al contenido y, desde el contenido, izquierda sin más a la izquierda entra en ella. */
    _moveSide(current, direction, layer) {
        const nav = document.querySelector('.appnav:not(.is-hidden)');
        if (nav.contains(current)) {
            const items = this._navItems(layer);
            const i = items.indexOf(current);
            if (direction === 'up' || direction === 'down') {
                const next = items[i + (direction === 'down' ? 1 : -1)];
                if (next) this.focus(next); else current.dispatchEvent(new CustomEvent('nav:edge', { bubbles: true, detail: { direction } }));
                return true;
            }
            if (direction === 'right') {
                const back = this._lastContent && document.contains(this._lastContent) && this._isVisible(this._lastContent) && layer.contains(this._lastContent) ? this._lastContent : null;
                const first = back || layer.querySelector('[data-nav-default]') || this.candidates(layer).map((c) => c.el).find((el) => !nav.contains(el));
                if (first) this.focus(first);
                return true;
            }
            return true; // izquierda: no hay nada más a la izquierda de la barra
        }
        if (direction === 'left') {
            const next = this.findInList(current, direction) || this.findNext(current, direction, layer);
            if (next) { this.focus(next); return true; }
            const items = this._navItems(layer);
            // La entrada de la sección actual (la marca de arriba también lleva «is-active» en Inicio: se prefiere la entrada de la lista).
            const here = items.find((el) => el.classList.contains('appnav-link') && el.classList.contains('is-active')) || items.find((el) => el.classList.contains('is-active')) || items[0];
            if (here) this.focus(here);
            return true;
        }
        return false;
    }

    move(direction) {
        const layer = this.layer();
        let current = this.current && this._inLayer(layer, this.current) && this._isVisible(this.current) ? this.current : null;
        if (!current) {
            const active = document.activeElement;
            if (active && active !== document.body && this._inLayer(layer, active)) current = active;
        }
        if (!current) {
            this.focusLayer(layer);
            return;
        }
        if (this._sideNav() && this._moveSide(current, direction, layer)) return;
        // Fila con botón propio (canal, lista de películas: «+» de Mi lista): derecha entra en el botón, izquierda vuelve a la fila.
        const inner = this._rowInner(current, direction);
        if (inner) { this.focus(inner); return; }
        const listNext = this.findInList(current, direction);
        const next = listNext || this.findNext(current, direction, layer);
        if (next) {
            this.focus(next);
        } else {
            current.dispatchEvent(new CustomEvent('nav:edge', { bubbles: true, detail: { direction } }));
        }
    }

    _rowInner(current, direction) {
        if (direction === 'right' && current.matches('.chitem, .mitem')) {
            const b = current.querySelector('[data-fav]');
            return b && !b.disabled && this._isVisible(b) && b.getAttribute('tabindex') !== '-1' ? b : null;
        }
        if (current.matches('[data-fav]')) {
            const row = current.closest('.chitem, .mitem');
            if (!row) return null;
            if (direction === 'left') return row;
            // Arriba y abajo desde el «+» pasan al «+» de la fila anterior o siguiente (si no, al elemento de debajo).
            if (direction === 'up' || direction === 'down') {
                const sib = direction === 'down' ? row.nextElementSibling : row.previousElementSibling;
                if (sib && sib.matches('.chitem, .mitem')) {
                    const b = sib.querySelector('[data-fav]');
                    return b && b.getAttribute('tabindex') !== '-1' ? b : sib;
                }
            }
        }
        return null;
    }

    /** En una lista vertical, el vecino inmediato; null si no está en una o si es su primer/último elemento. */
    findInList(current, direction) {
        if (direction !== 'up' && direction !== 'down') return null;
        const list = current.closest(LIST_SELECTOR);
        if (!list) return null;
        const items = this.candidates(list).map((c) => c.el).filter((el) => el.closest(LIST_SELECTOR) === list);
        const i = items.indexOf(current);
        if (i === -1) return null;
        return items[i + (direction === 'down' ? 1 : -1)] || null;
    }

    findNext(current, direction, layer) {
        const list = this.candidates(layer);
        const cur = current.getBoundingClientRect();
        const cx = cur.left + cur.width / 2;
        const cy = cur.top + cur.height / 2;
        const currentRow = current.closest('[data-nav-row]');
        const fromContent = this._sideNav() && !current.closest('.appnav');
        const horizontal = direction === 'left' || direction === 'right';
        let best = null;
        let bestScore = Infinity;
        const viable = [];

        for (let i = 0; i < list.length; i++) {
            const { el, rect } = list[i];
            if (el === current || current.contains(el) || el.contains(current)) continue;
            if (fromContent && el.closest('.appnav')) continue; // con barra lateral, a ella solo se entra con «izquierda» (ver _moveSide)
            const x = rect.left + rect.width / 2;
            const y = rect.top + rect.height / 2;
            const dx = x - cx;
            const dy = y - cy;

            if (direction === 'left' && rect.right > cur.left + 1) continue;
            if (direction === 'right' && rect.left < cur.right - 1) continue;
            if (direction === 'up' && rect.bottom > cur.top + 1) continue;
            if (direction === 'down' && rect.top < cur.bottom - 1) continue;

            const row = el.closest('[data-nav-row]');
            // Dentro de una fila horizontal, izquierda/derecha se quedan en la fila.
            // Salvo que la otra fila esté en la misma línea (p. ej. los filtros y, a su derecha, «Sorpréndeme» y el modo de vista).
            if (horizontal && currentRow && row !== currentRow && Math.abs(dy) > Math.max(cur.height, rect.height) * 0.6) continue;
            // Arriba/abajo desde una fila salen de ella (no saltan a otra tarjeta de la misma fila).
            if (!horizontal && currentRow && row === currentRow) continue;

            const primary = horizontal ? Math.abs(dx) : Math.abs(dy);
            const secondary = horizontal ? Math.abs(dy) : Math.abs(dx);
            viable.push({ el, row, primary, secondary });
            const overlap = horizontal
                ? Math.max(0, Math.min(cur.bottom, rect.bottom) - Math.max(cur.top, rect.top))
                : Math.max(0, Math.min(cur.right, rect.right) - Math.max(cur.left, rect.left));
            let score = primary + secondary * 2;
            if (overlap > 0) score -= Math.min(overlap, 200);
            if (score < bestScore) {
                bestScore = score;
                best = el;
            }
        }
        // Una fila marcada con `data-nav-reach` (filtros, acciones de una tarjeta) que queda a medio camino no se
        // salta aunque esté desalineada en horizontal: la puntuación por alineación la dejaba fuera y no se llegaba.
        if (!horizontal && best) {
            const bestPrimary = (viable.find((v) => v.el === best) || {}).primary;
            let alt = null;
            for (let i = 0; i < viable.length; i++) {
                const v = viable[i];
                if (!v.row || !v.row.hasAttribute('data-nav-reach') || v.row === currentRow || !(v.primary < bestPrimary - 8)) continue;
                if (!alt || v.primary < alt.primary - 8 || (Math.abs(v.primary - alt.primary) <= 8 && v.secondary < alt.secondary)) alt = v;
            }
            if (alt) best = alt.el;
        }
        return best;
    }
}

export const tvnav = new TvNav();
